import { Link, useLocation } from 'react-router-dom';

const TermsOfUsePage = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const hideLoginBtn = urlParams.get('hideLoginBtn') === 'true';

  return (
    <div className="min-h-screen bg-[#006B82] py-8 px-4 sm:px-6">
      <div className="light max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-8">
        <div className="mb-8 border-b border-gray-200 pb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Terms & Conditions
          </h1>
          <p className="text-gray-600">
            Welcome to Gio. By accessing or using the Gio platform
            (&quot;Service&quot;), you agree to be bound by these Terms &
            Conditions.
          </p>
        </div>

        <div className="space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              About Gio
            </h2>
            <p>
              Gio (&quot;the Platform&quot;) is a wholly owned subsidiary and
              product of BK★CODE LLC, a New York limited liability company.
            </p>
            <p className="mt-2">
              BK★CODE LLC owns and operates Gio and all associated intellectual
              property, trademarks, software, services, and related assets.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              1. Overview
            </h2>
            <p className="mb-2">
              Gio is a business-to-business technology platform that enables
              users to place, manage, and fulfill orders through independent
              operators and their networks.
            </p>
            <p className="mb-2">
              Gio acts solely as a technology platform and does not act as a
              supplier, vendor, distributor, or logistics provider.
            </p>
            <p className="mb-2">
              At this time, Gio does not process payments directly. Payments may
              be handled outside the platform.
            </p>
            <p>
              Gio may, in the future, integrate third-party payment providers to
              facilitate transactions. Any such services will be subject to the
              terms and policies of those third-party providers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              2. Eligibility & Accounts
            </h2>
            <p className="mb-2">By using Gio, you represent that:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                You have the authority to act on behalf of yourself or your
                business
              </li>
              <li>All information provided is accurate and complete</li>
              <li>You will keep your account credentials secure</li>
            </ul>
            <p className="mt-2">
              You are responsible for all activity under your account.
            </p>
            <p className="mt-2">
              Gio does not verify or guarantee the legal status of any user or
              business on the platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              3. Use of Service
            </h2>
            <p className="mb-2">You agree to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the platform only for lawful business purposes</li>
              <li>Not interfere with or disrupt the platform</li>
              <li>
                Not attempt to reverse engineer, copy, or exploit the system
              </li>
              <li>
                Not misuse the platform for fraudulent or abusive activity
              </li>
            </ul>
            <p className="mt-2">
              Gio reserves the right to suspend or terminate accounts at its
              discretion.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              4. Orders & Fulfillment
            </h2>
            <p className="mb-2">
              Orders placed through Gio are fulfilled by independent parties.
            </p>
            <p className="mb-2">
              Gio does not guarantee availability, pricing accuracy, or
              fulfillment timelines. Items may be modified, substituted, or
              removed during fulfillment.
            </p>
            <p>
              Gio is not responsible for the quality, condition, legality, or
              availability of goods or services provided by users.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              5. Pricing & Payments
            </h2>
            <p className="mb-2">
              Prices may change during fulfillment. Final invoices are
              determined after order completion.
            </p>
            <p className="mb-2">
              Payments are currently handled outside the platform unless
              otherwise specified.
            </p>
            <p className="mb-2">If payment processing is introduced:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Transactions may be facilitated through third-party providers
              </li>
              <li>
                Gio is not responsible for payment processing errors, delays, or
                disputes
              </li>
            </ul>
            <p className="mt-2">
              All payment disputes are between the transacting parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              6. Platform Availability
            </h2>
            <p className="mb-2">
              Gio is provided on an &quot;as is&quot; and &quot;as
              available&quot; basis. We do not guarantee uninterrupted, secure,
              or error-free operation.
            </p>
            <p className="mb-2">Gio is not responsible for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>System outages</li>
              <li>Technical failures</li>
              <li>Data loss</li>
              <li>Network or infrastructure issues</li>
              <li>Delays caused by platform downtime</li>
            </ul>
            <p className="mt-2">Use of the platform is at your own risk.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              7. Data & Deletion
            </h2>
            <p className="mb-2">
              Users may request deletion of their data at any time.
            </p>
            <p className="mb-2">
              Gio will delete or anonymize data where reasonably possible.
            </p>
            <p>
              Certain data may be retained for legal, accounting, or operational
              purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              8. Limitation of Liability
            </h2>
            <p className="mb-2">
              To the fullest extent permitted by law, Gio and its affiliates
              shall not be liable for:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Loss of profits or revenue</li>
              <li>Business interruption</li>
              <li>Loss of data</li>
              <li>Indirect, incidental, or consequential damages</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              9. Indemnification
            </h2>
            <p className="mb-2">
              You agree to indemnify and hold harmless Gio from any claims,
              damages, or disputes arising from:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your use of the platform</li>
              <li>Your business operations</li>
              <li>Transactions conducted through the platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              10. Modifications
            </h2>
            <p>
              Gio may update these Terms at any time. Continued use of the
              platform constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              11. Governing Law
            </h2>
            <p>
              These Terms shall be governed by and construed in accordance with
              the laws of the State of New York, without regard to conflict of
              law principles.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              12. Contact
            </h2>
            <p>
              For questions regarding these Terms:{' '}
              <a
                href="mailto:app@giotheapp.com"
                className="text-[#006B82] hover:underline"
              >
                app@giotheapp.com
              </a>
            </p>
          </section>
        </div>

        {!hideLoginBtn && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <Link
              to="/login"
              className="inline-flex items-center text-[#006B82] hover:text-[#00809C] transition-colors font-medium"
            >
              ← Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default TermsOfUsePage;
